<template>
  <framed-table :striped="false">
    <caption class="pi-sr">{{ caption }}</caption>
    <thead>
    <tr>
      <template v-for="column in headerColumns">
        <pi-sort-th v-if="column.sortable" :key="column.key" :column="column" :state="sortState" :disabled="loading" @sort="$emit('sort', $event)"/>
        <pi-th v-else :key="column.key" :column="column"/>
      </template>
    </tr>
    </thead>
    <tbody v-for="row in rows" :key="row.key">
    <tr :class="{ 'pi-row-selected': expanded === row.key }">
      <template v-for="column in headerColumns">
        <td v-if="column.key === 'name'" :key="column.key" class="pi-obt__name">
          <button type="button" class="pi-obt__toggle" :aria-expanded="expanded === row.key ? 'true' : 'false'"
                  :aria-label="(expanded === row.key ? 'Esconder' : 'Mostrar') + ' os marcos de ' + row.name" @click="toggle(row.key)">
            {{ expanded === row.key ? '▾' : '▸' }}
          </button>
          <pi-company-name :company-id="row.companyId" :shard-code="row.shardCode" :name="row.name" :query="query" :origin="origin" stacked/>
        </td>
        <td v-else-if="column.key === 'soldAt'" :key="column.key">
          {{ row.soldText }}
          <span v-if="row.approximate" class="pi-badge pi-badge--info" :title="row.soldSource.label">D0 aproximado</span>
          <span v-if="row.unsold" class="pi-badge pi-badge--muted" :title="row.soldSource.label">não vendida</span>
        </td>
        <td v-else-if="column.key === 'daysSinceSale'" :key="column.key" class="is-num">{{ row.daysText }}</td>
        <td v-else-if="column.key === 'stage'" :key="column.key">
          {{ row.stageText }}
          <span v-if="row.skippedSetup" class="pi-table__sub">pulou a configuração</span>
        </td>
        <td v-else-if="column.key === 'reason'" :key="column.key">{{ row.reasonText || '—' }}</td>
        <td v-else-if="column.key === 'overdueDays'" :key="column.key" class="is-num">{{ row.overdueText || '—' }}</td>
        <td v-else-if="column.key === 'nextStep'" :key="column.key">{{ row.nextStep || '—' }}</td>
        <td v-else-if="column.key === 'employees'" :key="column.key" class="is-num">
          {{ row.windowText || '—' }}
          <span v-if="row.windowStageText" class="pi-table__sub">{{ row.windowStageText }}</span>
        </td>
        <td v-else-if="column.key === 'seats'" :key="column.key" class="is-num">
          {{ row.seatsText }}
          <span v-if="row.seatsShareText" class="pi-table__sub">{{ row.seatsShareText }}</span>
          <span v-if="row.registeredText" class="pi-table__sub">{{ row.registeredText }}</span>
        </td>
        <td v-else-if="column.key === 'punches'" :key="column.key">{{ row.punchesText }}</td>
        <td v-else-if="column.key === 'punch7d'" :key="column.key" class="is-num">{{ row.punch7dText }}</td>
        <td v-else-if="column.key === 'adminLastActive'" :key="column.key">{{ row.adminText }}</td>
        <td v-else-if="column.key === 'lastProgress'" :key="column.key">{{ row.progressText }}</td>
        <td v-else :key="column.key">{{ row.profileText }}</td>
      </template>
    </tr>
    <tr v-if="expanded === row.key" class="pi-obt__detail">
      <td :colspan="headerColumns.length">
        <pi-onboarding-checklist :milestones="row.milestones" :skipped-setup="row.skippedSetup" compact/>
        <p class="pi-obt__facts">
          <span>Venda: {{ row.soldText }} · {{ row.soldSource.label }}</span>
          <span>Trial antes da venda: {{ row.trialText }}</span>
          <span v-if="row.employeesNowText">{{ row.employeesNowText }}</span>
          <span v-if="row.featuresText">{{ row.featuresText }}</span>
          <span v-if="row.originText">Origem: {{ row.originText }}</span>
          <span>Situação hoje: <span class="pi-badge" :class="'pi-badge--' + row.status.tone">{{ row.status.label }}</span></span>
          <router-link v-if="row.accountRoute" :to="row.accountRoute" class="pi-link">Abrir no Master</router-link>
        </p>
      </td>
    </tr>
    </tbody>
    <table-pager slot="footer" :total="total" :page="page" :page-size="pageSize"
                 @update:page="$emit('update:page', $event)" @update:pageSize="$emit('update:pageSize', $event)"/>
  </framed-table>
</template>

<script>
import framedTable from "../default/framedTable.vue";
import tablePager from "../default/tablePager.vue";
import piSortTh from "./piSortTh.vue";
import piTh from "./piTh.vue";
import piCompanyName from "./piCompanyName.vue";
import piOnboardingChecklist from "./piOnboardingChecklist.vue";
import { screenColumns } from "./piColumns.js";
import { ACTIVATION_ORIGIN } from "./piNavigation.js";

export default {
  name: "pi-onboarding-company-table",
  components: { framedTable, tablePager, piSortTh, piTh, piCompanyName, piOnboardingChecklist },
  props: {
    rows: { type: Array, default: () => [] },
    columns: { type: Array, default: () => [] },
    sortState: { type: Object, default: null },
    total: { type: Number, default: 0 },
    page: { type: Number, default: 1 },
    pageSize: { type: Number, default: 25 },
    query: { type: Object, default: () => ({}) },
    caption: { type: String, default: "Empresas" },
    loading: { type: Boolean, default: false },
  },
  data() {
    return { expanded: "", origin: ACTIVATION_ORIGIN };
  },
  computed: {
    headerColumns() {
      return screenColumns(this.columns);
    },
  },
  watch: {
    rows() {
      this.expanded = "";
    },
  },
  methods: {
    toggle(key) {
      this.expanded = this.expanded === key ? "" : key;
    },
  },
};
</script>

<style src="./pi.css"></style>

<style scoped>
.pi-obt__name {
  min-width: 200px;
}

.pi-obt__toggle {
  width: 20px;
  height: 20px;
  margin-right: 4px;
  padding: 0;
  border: 1px solid #c5cad3;
  border-radius: 3px;
  background: #ffffff;
  color: #3a424e;
  font-size: 11px;
  line-height: 18px;
  cursor: pointer;
}

.pi-obt__detail > td {
  background: #fafbfc;
}

.pi-obt__facts {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 18px;
  margin: 8px 0 0;
  font-size: 12px;
  color: #4e5560;
}
</style>



// WEBPACK FOOTER //
// src/vue/pages/components/pi/piOnboardingCompanyTable.vue